import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UserPlus, Users, History, LayoutDashboard } from "lucide-react";

import { squadService } from "@/services/api/squad.service";

import { PessoaDTO } from "@/types/pessoa";
import { SquadDTO } from "@/types/squad.type";
import { PermissaoEnum } from "@workflow/types";

import { useUserRolesData } from "@/hooks/use-user-roles-data";
import { usePermissions } from "@/hooks/use-permissions";
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { pessoasKeys } from "@/services/api/queries/pessoas.queries";

import { TopBarSection } from "@/components/shared/SecaoBarraSuperior";
import { PageTabs, PageTabItem } from "@/components/shared/PageTabs";
import { PessoaDialogEdit } from "./PessoaDialogEdit";
import { UserListTable } from "./UserListTable";
import { UserResumoCards } from "./UserResumoCards";
import { AcessosDataTable } from "./components/AcessosDataTable";
import { PainelTab } from "./components/PainelTab";

const UserListaPage = () => {
  const queryClient = useQueryClient();
  const { hasPermission, isAdmin } = usePermissions();
  const { goToUsuarioCreate } = useNavigationHelpers();
  const canEditRoles = hasPermission(PermissaoEnum.PESSOA_ADD_ROLE);

  // `useUserRolesData` continua servindo os dados auxiliares dos diálogos
  // (lista de perfis e de pessoas para substituto). A grid abaixo busca a
  // listagem paginada server-side por conta própria.
  const { users, roles, refreshUsers } = useUserRolesData();

  // Refaz a grid (query server-side) e os dados auxiliares após uma mutação.
  const refreshAll = async () => {
    queryClient.invalidateQueries({ queryKey: pessoasKeys.list });
    queryClient.invalidateQueries({ queryKey: pessoasKeys.resumo });
    await refreshUsers();
  };

  // Squads State
  const [squads, setSquads] = useState<SquadDTO[]>([]);

  useEffect(() => {
    async function loadSquads() {
      try {
        const response = await squadService.getAll();
        setSquads(Array.isArray(response) ? response : (response as any).data || []);
      } catch (error) {
        console.error("Erro ao buscar squads:", error);
      }
    }
    loadSquads();
  }, []);

  // --- Lógica: Editar Usuário ---
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PessoaDTO | null>(null);
  const [editingUserRoleIds, setEditingUserRoleIds] = useState<number[]>([]);
  const [editingUserSquadIds, setEditingUserSquadIds] = useState<number[]>([]);

  const openEditUserRolesDialog = (user: PessoaDTO) => {
    setEditingUser(user);
    setEditingUserRoleIds(user.roles?.map((r) => r.id) || []);
    setEditingUserSquadIds(
      (user.squads?.map((squad) => squad.id).filter((id) => id !== undefined) as number[]) || [],
    );
    setIsEditUserDialogOpen(true);
  };

  const handleRoleToggleForEditUser = (roleId: number) => {
    setEditingUserRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  };

  const handleSquadToggleForEditUser = (squadId: number) => {
    setEditingUserSquadIds((prev) =>
      prev.includes(squadId) ? prev.filter((id) => id !== squadId) : [...prev, squadId],
    );
  };

  const handleSaveUserRoles = async () => {
    await refreshAll();
    setIsEditUserDialogOpen(false);
  };

  const tabs: PageTabItem[] = [
    {
      value: "usuarios",
      label: "Usuários",
      icon: Users,
      content: (
        <div className="space-y-6">
          <UserResumoCards />
          <div className="min-h-[400px] transition-all duration-300">
            <UserListTable onEditUser={openEditUserRolesDialog} canEdit={canEditRoles} />
          </div>
        </div>
      ),
    },
    { value: "painel", label: "Painel", icon: LayoutDashboard, content: <PainelTab /> },
    { value: "acessos", label: "Acessos", icon: History, content: <AcessosDataTable /> },
  ];

  return (
    <div className="flex flex-col space-y-6 pb-10 duration-500 animate-in fade-in">
      <TopBarSection
        entity="Equipe"
        title="Gestão de Usuários"
        subtitle="Gerencie o acesso, perfis e alocação de squads da sua equipe."
        showButton
        buttonLabel="Novo Usuário"
        buttonIcon={<UserPlus className="h-4 w-4" />}
        onButtonClick={() => goToUsuarioCreate()}
        buttonPermission={PermissaoEnum.PESSOA_CREATE}
      />

      {isAdmin ? (
        <PageTabs tabs={tabs} paramKey="tab" />
      ) : (
        <>
          <UserResumoCards />
          <div className="min-h-[400px] transition-all duration-300">
            <UserListTable onEditUser={openEditUserRolesDialog} canEdit={canEditRoles} />
          </div>
        </>
      )}

      <PessoaDialogEdit
        open={isEditUserDialogOpen}
        onOpenChange={setIsEditUserDialogOpen}
        roles={roles}
        squads={squads}
        pessoas={users}
        editingUserRoleIds={editingUserRoleIds}
        editingUserSquadIds={editingUserSquadIds}
        onRoleToggle={handleRoleToggleForEditUser}
        onSquadToggle={handleSquadToggleForEditUser}
        onSave={handleSaveUserRoles}
        onCancel={() => setIsEditUserDialogOpen(false)}
        editingUser={editingUser}
      />
    </div>
  );
};

export default UserListaPage;
